import Papa from "papaparse";
import * as XLSX from "xlsx";
import { isValidEmail } from "@somasi/shared";

export type ParsedRow = Record<string, string>;

export async function parseFile(file: File): Promise<ParsedRow[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<ParsedRow>(ws, { defval: "", raw: false });
    return rows.map(normalize);
  }
  // csv
  const text = await file.text();
  const parsed = Papa.parse<ParsedRow>(text, { header: true, skipEmptyLines: true });
  return parsed.data.map(normalize);
}

function normalize(row: ParsedRow): ParsedRow {
  const out: ParsedRow = {};
  for (const k of Object.keys(row)) {
    out[k.trim()] = typeof row[k] === "string" ? (row[k] as string).trim() : (row[k] as string);
  }
  return out;
}

/**
 * A spreadsheet's many spellings of "yes".
 *
 * Operators tick boxes with whatever is to hand, and Excel's own tick is a
 * literal "x" or "✓" in the cell — so a column meant as a flag arrives as any
 * of these. Anything unrecognised is false, which is the safe reading for the
 * flags this imports: a service nobody confirmed applies is one that does not.
 */
export function parseBool(value: string | undefined): boolean {
  if (!value) return false;
  return ["yes", "y", "true", "1", "x", "✓"].includes(String(value).trim().toLowerCase());
}

/** Blank, or an address that passes the shared rule the forms and the API use. */
export function emailIsValid(e: string): boolean {
  if (!e.trim()) return true;
  return isValidEmail(e);
}

export function dateIsValid(d: string): boolean {
  if (!d) return true;
  return !isNaN(Date.parse(d));
}

/** Download a CSV template. */
export function downloadTemplate(
  filename: string,
  headers: string[],
  sampleRow?: Record<string, string>,
) {
  const csv = Papa.unparse({
    fields: headers,
    data: sampleRow ? [headers.map((h) => sampleRow[h] ?? "")] : [],
  });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
